export const getNextTagSelection = ({
  allCodes = [],
  selectedTags = [],
  code,
  mode,
} = {}) => {
  const isOnlySelected =
    selectedTags.length === 1 && selectedTags[0] === code;

  if (isOnlySelected && allCodes.length > 1) {
    return allCodes.filter((item) => item !== code);
  }

  const allSelected =
    allCodes.length > 0 && selectedTags.length === allCodes.length;
  let newSelection = [...selectedTags];

  if (allSelected && selectedTags.includes(code)) {
    newSelection = [code];
  } else if (mode === "exclusive") {
    newSelection = [code];
  } else if (mode === "unselect") {
    newSelection = selectedTags.filter((tag) => tag !== code);
    if (newSelection.length === 0) {
      newSelection = allCodes;
    }
  } else if (selectedTags.includes(code)) {
    newSelection = selectedTags.filter((tag) => tag !== code);
    if (newSelection.length === 0) {
      newSelection = allCodes;
    }
  } else {
    newSelection = [...selectedTags, code];
  }

  return newSelection;
};

const hasAllSelected = (selectedItems = [], allItems = []) => {
  if (allItems.length === 0) return false;
  if (selectedItems.length !== allItems.length) return false;
  const allSet = new Set(allItems);
  return selectedItems.every((item) => allSet.has(item));
};

export const getTagsForLayers = (taxonomies = [], layerIds = []) => {
  if (!Array.isArray(taxonomies) || taxonomies.length === 0) return [];
  if (!Array.isArray(layerIds) || layerIds.length === 0) return [];
  const layerSet = new Set(layerIds);
  return taxonomies
    .filter((tax) => layerSet.has(tax.layer_id))
    .map((tax) => tax.code)
    .filter(Boolean);
};

export const getNextLayerSelection = ({
  allLayerIds = [],
  selectedLayers = [],
  layerId,
} = {}) => {
  const isOnlySelected =
    selectedLayers.length === 1 && selectedLayers[0] === layerId;

  if (isOnlySelected && allLayerIds.length > 1) {
    return allLayerIds.filter((id) => id !== layerId);
  }

  const allSelected =
    allLayerIds.length > 0 && selectedLayers.length === allLayerIds.length;

  if (allSelected && selectedLayers.includes(layerId)) {
    return [layerId];
  }

  if (selectedLayers.includes(layerId)) {
    if (selectedLayers.length === 1) {
      return allLayerIds;
    }
    return selectedLayers.filter((id) => id !== layerId);
  }

  return [...selectedLayers, layerId];
};

export const getNextLayerFilterState = ({
  taxonomies = [],
  allLayerIds = [],
  selectedLayers = [],
  selectedTags = [],
  layerId,
} = {}) => {
  const activeLayers = selectedLayers.length > 0 ? selectedLayers : allLayerIds;
  const currentTagCodes = getTagsForLayers(taxonomies, activeLayers);
  const hasTagFilter = !hasAllSelected(selectedTags, currentTagCodes);
  const nextSelectedLayers = getNextLayerSelection({
    allLayerIds,
    selectedLayers: activeLayers,
    layerId,
  });
  const nextTagCodes = getTagsForLayers(taxonomies, nextSelectedLayers);

  if (!hasTagFilter) {
    return { selectedLayers: nextSelectedLayers, selectedTags: nextTagCodes };
  }

  const nextTagSet = new Set(nextTagCodes);
  let nextSelectedTags = selectedTags.filter((tag) => nextTagSet.has(tag));
  if (nextSelectedTags.length === 0) {
    nextSelectedTags = nextTagCodes;
  }

  return { selectedLayers: nextSelectedLayers, selectedTags: nextSelectedTags };
};

export const getNextTagFilterState = ({
  taxonomies = [],
  allLayerIds = [],
  selectedLayers = [],
  selectedTags = [],
  code,
  mode,
} = {}) => {
  const activeLayers = selectedLayers.length > 0 ? selectedLayers : allLayerIds;
  const tagLayerId = taxonomies.find((tax) => tax.code === code)?.layer_id;
  const isLayerInactive =
    tagLayerId && !activeLayers.includes(tagLayerId);

  let nextSelectedLayers = activeLayers;
  if (isLayerInactive) {
    const layerSet = new Set([...activeLayers, tagLayerId]);
    nextSelectedLayers = allLayerIds.filter((id) => layerSet.has(id));
  }

  const nextActiveTagCodes = getTagsForLayers(taxonomies, nextSelectedLayers);
  const nextActiveTagSet = new Set(nextActiveTagCodes);
  let normalizedSelectedTags = selectedTags.filter((tag) =>
    nextActiveTagSet.has(tag)
  );

  if (isLayerInactive) {
    const tagLayerCodes = getTagsForLayers(taxonomies, [tagLayerId]);
    const tagLayerSet = new Set(tagLayerCodes);
    normalizedSelectedTags = normalizedSelectedTags.filter(
      (tag) => !tagLayerSet.has(tag)
    );
  }

  const nextSelectedTags = getNextTagSelection({
    allCodes: nextActiveTagCodes,
    selectedTags: normalizedSelectedTags,
    code,
    mode,
  });

  return { selectedLayers: nextSelectedLayers, selectedTags: nextSelectedTags };
};
